import { useRef, useState } from 'react';
import { useOverlay } from './Overlay';
import { BugIcon } from './Icons';
import { sendFeedback } from '@/lib/feedback';

// The bug icon on the four main screens (Gary, 19 Oct): anyone reports something not working, or suggests an
// improvement. It goes to the Feedback channel in Team for head office, with the screen they were on.

// The unsent report outlives the sheet: Back, a swipe or a tap outside used to throw the typed text away. It is
// cleared only once the report is sent (mobile review, 19 Oct).
type Draft = { kind: 'bug' | 'idea'; text: string; photo: File | null };
const emptyDraft = (): Draft => ({ kind: 'bug', text: '', photo: null });
let draft: Draft = emptyDraft();

function ReportSheet({ onSent }: { onSent: () => void }) {
  const [kind, setKindState] = useState<'bug' | 'idea'>(draft.kind);
  const [text, setTextState] = useState(draft.text);
  const [photo, setPhotoState] = useState<File | null>(draft.photo);
  const setKind = (k: 'bug' | 'idea') => { draft = { ...draft, kind: k }; setKindState(k); };
  const setText = (t: string) => { draft = { ...draft, text: t }; setTextState(t); };
  const setPhoto = (f: File | null) => { draft = { ...draft, photo: f }; setPhotoState(f); };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const send = () => {
    setBusy(true); setError(null);
    sendFeedback(kind, text.trim(), photo).then(() => { draft = emptyDraft(); onSent(); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : 'That did not send. Try again.'); setBusy(false); });
  };
  return (
    <div className="chats-pad chats-report">
      <h2 className="chats-sheet-title">Report or suggest</h2>
      <div className="chats-chips" role="radiogroup" aria-label="Kind">
        <button type="button" role="radio" aria-checked={kind === 'bug'} className={`chats-chip${kind === 'bug' ? ' active' : ''}`} onClick={() => setKind('bug')}>Not working</button>
        <button type="button" role="radio" aria-checked={kind === 'idea'} className={`chats-chip${kind === 'idea' ? ' active' : ''}`} onClick={() => setKind('idea')}>Idea</button>
      </div>
      {error && <p className="chats-rec-error" role="alert">{error}</p>}
      <textarea className="chats-input chats-input--stack" rows={4} value={text} onChange={(e) => setText(e.target.value)} autoFocus
        placeholder={kind === 'bug' ? 'What happened, and what did you expect?' : 'What would make the app better?'} aria-label="Your report" />
      <input ref={picker} type="file" accept="image/png,image/jpeg" className="chats-file-picker" tabIndex={-1} aria-hidden="true"
        onChange={(e) => { setPhoto(e.target.files?.[0] ?? null); e.target.value = ''; }} />
      <button type="button" className="chats-btn chats-btn--ghost chats-rec-wide" onClick={() => picker.current?.click()}>{photo ? `Screenshot added: ${photo.name}` : 'Add a screenshot'}</button>
      <button type="button" className="chats-btn chats-rec-wide" disabled={busy || text.trim().length < 3} onClick={send}>{busy ? 'Sending…' : 'Send to head office'}</button>
    </div>
  );
}

export function ReportButton() {
  const { openSheet, closeSheet, toast } = useOverlay();
  return (
    // In the header from tablet width; on a phone it is in Settings (Gary, 3 Nov 2026: the header was too busy).
    <button type="button" className="chats-listhead-help chats-listhead-desk" aria-label="Report a problem or suggest an idea" title="Report a problem or suggest an idea"
      onClick={() => openSheet(<ReportSheet onSent={() => { closeSheet(); toast('Sent to head office. Thank you.'); }} />)}>
      <BugIcon />
    </button>
  );
}

/** The same report, as a row of Settings on a phone, where it moved from the header (Gary, 3 Nov 2026). */
export function ReportRow() {
  const { openSheet, closeSheet, toast } = useOverlay();
  return (
    <button type="button" className="chats-set chats-me-phone" onClick={() => openSheet(<ReportSheet onSent={() => { closeSheet(); toast('Sent to head office. Thank you.'); }} />)}>
      <div className="chats-set-main">
        Report a problem or idea
        <small>Straight to head office</small>
      </div>
    </button>
  );
}
